import { ArgsScopedAccessor } from '@passionware/platform-storybook';
import { generatePath } from 'react-router-dom';
import { RoutingService } from './RoutingService';

export function createRoutingService(
  accessor: ArgsScopedAccessor<string>
): RoutingService {
  const service: RoutingService = {
    resolvePathParams: (path: string) => {
      const currentPath = accessor.get();
      return resolvePathParams(path, currentPath);
    },
    useResolvePathParams: () => {
      const currentPath = accessor.use();

      return (path: string) => resolvePathParams(path, currentPath);
    },
    resolveLink: (path: string) => {
      const resolved = service.resolvePathParams(path);

      try {
        generatePath(resolved);
      } catch (error) {
        throw new Error(`Unresolved path parameters in path: ${path}`);
      }
      return resolved;
    },
    useResolveLink: () => {
      const resolvePathParams = service.useResolvePathParams();

      return (path: string) => {
        const resolved = resolvePathParams(path);

        try {
          generatePath(resolved);
        } catch (error) {
          throw new Error(`Unresolved path parameters in path: ${path}`);
        }
        return resolved;
      };
    }
  };
  return service;
}

// Note: We do not use generatePath from react-router directly here because:
// 1. generatePath requires all parameters to be present, and throws if any are missing.
// 2. It does not support partial parameter substitution—if a value is missing, it cannot leave the placeholder in the result.
// 3. It does not handle catch-all parameters (e.g., :param*) with custom encoding or partial matching as our use case requires.
// 4. We need to preserve trailing slashes and handle cases where literal segments do not match, returning the original path.
// This custom implementation allows us to substitute as many parameters as possible, leave placeholders for missing ones, handle catch-alls, and preserve slashes, matching our test and product requirements.
export function resolvePathParams(path: string, currentPath: string): string {
  if (!path) return path;
  if (!currentPath) return path;

  const hasTrailingSlash = path.endsWith('/');
  const pathSegments = path.split('/');
  const currentPathClean = currentPath.split(/[?#]/)[0];
  const currentSegments = currentPathClean.split('/');

  // First pass: collect parameter values using smart matching
  const paramValues = new Map<string, string | undefined>();
  let pathIndex = 0;
  let currentIndex = 0;
  let catchAllUsed = false;

  while (
    pathIndex < pathSegments.length &&
    currentIndex < currentSegments.length
  ) {
    const pathSegment = pathSegments[pathIndex];
    if (pathSegment.startsWith(':')) {
      const paramName = pathSegment.slice(1);
      if (paramName.endsWith('*') && !catchAllUsed) {
        // Catch-all parameter
        const remaining = currentSegments.slice(currentIndex).join('/');
        paramValues.set(paramName, encodeURIComponent(remaining));
        catchAllUsed = true;
        pathIndex++;
        currentIndex = currentSegments.length;
        break;
      } else {
        // Only substitute if the segment is non-empty
        if (currentIndex < currentSegments.length) {
          const value = currentSegments[currentIndex];
          if (value !== undefined && value !== '') {
            paramValues.set(paramName, value);
          }
        }
        pathIndex++;
        currentIndex++;
      }
    } else {
      // Literal segment - must match exactly at current position
      if (pathSegment === currentSegments[currentIndex]) {
        // Direct match
        pathIndex++;
        currentIndex++;
      } else {
        // Literal doesn't match - stop substituting but continue with placeholders
        break;
      }
    }
  }

  // Second pass: build result with substitutions
  const resultSegments: string[] = [];
  for (let i = 0; i < pathSegments.length; i++) {
    const pathSegment = pathSegments[i];
    if (pathSegment.startsWith(':')) {
      const paramName = pathSegment.slice(1);
      if (paramValues.has(paramName)) {
        resultSegments.push(paramValues.get(paramName) ?? pathSegment);
      } else {
        resultSegments.push(pathSegment);
      }
    } else {
      resultSegments.push(pathSegment);
    }
  }
  let result = resultSegments.join('/');
  if (!result.startsWith('/')) result = '/' + result;
  if (hasTrailingSlash && !result.endsWith('/')) {
    result += '/';
  }
  return result;
}
